/**
 * Attach From Device Handler
 * 
 * Handles file selection from the user's device with browser permissions.
 * Currently supports: txt, md files with multiple selection.
 * 
 * ARCHITECTURE:
 * - Clean separation: permissions -> file picker -> processing
 * - Injected implementation pattern for onFilesAdded
 * - Minimal, focused responsibility
 */

export interface AttachFromDeviceConfig {
  /**
   * Callback when files are successfully selected and read
   */
  onFilesAdded: (files: FileData[]) => Promise<void>;
  
  /**
   * Supported file types (MIME types)
   * @default ['text/plain', 'text/markdown']
   */
  supportedTypes?: string[];
  
  /**
   * Allow multiple file selection
   * @default true
   */
  allowMultiple?: boolean;
  
  /**
   * Enable debug logging
   * @default false
   */
  enableDebugLogging?: boolean;
}

export interface FileData {
  name: string;
  content: string;
  type: string;
  size: number;
  lastModified: number;
}

/**
 * Creates an attach-from-device handler with injected implementation
 */
export function createAttachFromDeviceHandler(config: AttachFromDeviceConfig) {
  const {
    onFilesAdded,
    supportedTypes = ['text/plain', 'text/markdown'],
    allowMultiple = true,
    enableDebugLogging = false,
  } = config;

  return async (): Promise<void> => {
    try {
      if (enableDebugLogging) {
        console.log('[AttachFromDevice] Starting file selection process');
      }

      // Check if File API is supported
      if (!window.File || !window.FileReader || !window.FileList) {
        throw new Error('File API not supported in this browser');
      }

      // Create file input element
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = allowMultiple;
      
      // Set accepted file types
      // Convert MIME types to file extensions for better UX
      const acceptExtensions = supportedTypes
        .map(type => {
          switch (type) {
            case 'text/plain': return '.txt';
            case 'text/markdown': return '.md';
            default: return '';
          }
        })
        .filter(ext => ext)
        .join(',');
      
      input.accept = acceptExtensions;

      if (enableDebugLogging) {
        console.log('[AttachFromDevice] File input configured:', {
          multiple: allowMultiple,
          accept: acceptExtensions,
          supportedTypes,
        });
      }

      // Create promise to handle file selection
      const fileSelectionPromise = new Promise<FileList | null>((resolve, reject) => {
        input.onchange = (event) => {
          const target = event.target as HTMLInputElement;
          resolve(target.files);
        };

        input.oncancel = () => {
          if (enableDebugLogging) {
            console.log('[AttachFromDevice] File selection cancelled by user');
          }
          resolve(null);
        };

        // Handle potential errors
        input.onerror = () => {
          reject(new Error('File input error'));
        };
      });

      // Trigger file picker
      input.click();

      // Wait for user selection
      const selectedFiles = await fileSelectionPromise;

      if (!selectedFiles || selectedFiles.length === 0) {
        if (enableDebugLogging) {
          console.log('[AttachFromDevice] No files selected');
        }
        return;
      }

      if (enableDebugLogging) {
        console.log('[AttachFromDevice] Files selected:', selectedFiles.length);
      }

      // Process selected files
      const fileDataArray: FileData[] = [];

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];

        // Validate file type
        if (!supportedTypes.includes(file.type) && file.type !== '') {
          // For files without MIME type, check extension
          const extension = file.name.toLowerCase().split('.').pop();
          const isSupported = (extension === 'txt' && supportedTypes.includes('text/plain')) ||
                             (extension === 'md' && supportedTypes.includes('text/markdown'));
          
          if (!isSupported) {
            console.warn(`[AttachFromDevice] Skipping unsupported file: ${file.name} (${file.type})`);
            continue;
          }
        }

        if (enableDebugLogging) {
          console.log('[AttachFromDevice] Processing file:', {
            name: file.name,
            type: file.type,
            size: file.size,
          });
        }

        // Read file content
        const content = await readFileAsText(file);

        fileDataArray.push({
          name: file.name,
          content,
          type: file.type,
          size: file.size,
          lastModified: file.lastModified,
        });
      }

      if (fileDataArray.length === 0) {
        if (enableDebugLogging) {
          console.log('[AttachFromDevice] No supported files found');
        }
        return;
      }

      // Call injected implementation
      if (enableDebugLogging) {
        console.log('[AttachFromDevice] Calling onFilesAdded with:', fileDataArray.length, 'files');
      }

      await onFilesAdded(fileDataArray);

      if (enableDebugLogging) {
        console.log('[AttachFromDevice] File processing completed successfully');
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('[AttachFromDevice] Error:', errorMessage);
      throw new Error(`Failed to attach files from device: ${errorMessage}`);
    }
  };
}

/**
 * Helper function to read file as text
 */
function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const result = event.target?.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Failed to read file as text'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${file.name}`));
    };
    
    reader.readAsText(file);
  });
}
